import { Validators } from '@angular/forms';
import {
  CreateSamplingProfileConfigDTO,
  SamplingProfileConfigDTO,
  SamplingProfileSignalConfigDTO,
  SamplingProfileType,
  SortDirection,
} from 'src/app/base/models/models.swagger.generated';
import {
  defineDynamicEntityDefinition,
  enumColumn,
  textColumn,
} from 'src/app/base/shared/dynamic/dynamic-entity-definition';

/**
 * Edit model for a sampling profile, and the payload the grid hands to the service on save.
 *
 * `signals` is a custom-rendered field holding the complete desired signal list — the backend diffs it
 * against the stored rows, so the profile is edited as one document.
 *
 * `profileType` only applies when creating (the scope is immutable afterwards), which is why this is a
 * dedicated model rather than the generated update DTO: the update DTO has no such field. The service
 * builds the real create/update DTOs from this model.
 */
export interface SamplingProfileEditModel {
  id: number;
  name: string;
  description?: string;
  profileType: SamplingProfileType;
  signals: SamplingProfileSignalConfigDTO[];
}

export const samplingProfileDynamicDefinition = defineDynamicEntityDefinition<
  SamplingProfileConfigDTO,
  CreateSamplingProfileConfigDTO,
  SamplingProfileEditModel
>({
  entityName: 'sampling-profile',
  primaryKey: 'id',
  defaultSort: { field: 'Name', direction: SortDirection.Ascending },
  grid: {
    searchFields: ['name', 'description'],
    columns: [
      {
        field: 'id',
        headerKey: 'i18n.domain.sampling-profiles.columns.id',
        type: 'number',
        visible: false,
        allowFiltering: false,
      },
      textColumn<SamplingProfileConfigDTO>('name', 'i18n.domain.sampling-profiles.columns.name'),
      textColumn<SamplingProfileConfigDTO>('description', 'i18n.domain.sampling-profiles.columns.description'),
      enumColumn<SamplingProfileConfigDTO>('profileType', 'i18n.domain.sampling-profiles.columns.profile-type', {
        enumType: SamplingProfileType,
        enumName: 'SamplingProfileType',
        width: '140px',
      }),
      textColumn<SamplingProfileConfigDTO>('ownerUserName', 'i18n.domain.sampling-profiles.columns.owner'),
      {
        field: 'signalCount',
        headerKey: 'i18n.domain.sampling-profiles.columns.signal-count',
        type: 'number',
        width: '120px',
        allowFiltering: false,
      },
      {
        field: 'vehicleCount',
        headerKey: 'i18n.domain.sampling-profiles.columns.vehicle-count',
        type: 'number',
        width: '120px',
        // Computed per page in a second query, so it is not a filterable/sortable column.
        allowFiltering: false,
        sortable: false,
      },
    ],
  },
  form: {
    fields: [
      {
        key: 'name',
        labelKey: 'i18n.domain.sampling-profiles.form.name',
        control: 'text',
        validators: [Validators.required, Validators.maxLength(200)],
        colMd: 6,
      },
      {
        key: 'profileType',
        labelKey: 'i18n.domain.sampling-profiles.form.profile-type',
        control: 'enum-select',
        enumOptions: {
          enumType: SamplingProfileType,
          enumName: 'SamplingProfileType',
        },
        defaultValue: SamplingProfileType.User,
        // Scope is immutable after creation: switching a profile between System and User would change
        // who it affects and who may edit it.
        readOnlyOnEdit: true,
        colMd: 6,
      },
      {
        key: 'description',
        labelKey: 'i18n.domain.sampling-profiles.form.description',
        control: 'textarea',
        validators: [Validators.maxLength(500)],
        colMd: 12,
      },
      {
        key: 'signals',
        labelKey: 'i18n.domain.sampling-profiles.form.signals',
        control: 'custom',
        defaultValue: [],
        colMd: 12,
      },
    ],
    layout: [
      { fields: ['name', 'profileType'] },
      { fields: ['description'] },
      { fields: ['signals'] },
    ],
  },
  mapToCreate: (formValue: SamplingProfileEditModel): CreateSamplingProfileConfigDTO => {
    return new CreateSamplingProfileConfigDTO({
      name: formValue.name!,
      description: formValue.description ?? undefined,
      profileType: formValue.profileType ?? SamplingProfileType.User,
      signals: formValue.signals ?? [],
    });
  },
  // The edit model is already the update payload; SamplingProfilesService turns it into the generated
  // UpdateSamplingProfileConfigDTO right where the API call happens.
  mapToUpdate: (formValue: SamplingProfileEditModel): SamplingProfileEditModel => formValue,
});
